import React, { useEffect, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from './AppIcon';

/**
 * How long the banner stays up on its own. Long enough to read a one-line sentence twice; the
 * close button is there for anyone who has read it sooner.
 */
export const AUTH_ERROR_HIDE_MS = 8000;

// One banner for the whole app, shown from anywhere: a tiny store rather than context, because
// the callers (OAuthHandler, the feed page's own token sync) are on opposite sides of the route
// tree and a failure can be reported before the banner has rendered at all.
let shownAt = null;
const listeners = new Set();
const notify = () => listeners.forEach((listener) => listener());
const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
const getSnapshot = () => shownAt;

/** Report a failed sign-in (#683). Replaces the old `alert()`; calling it again restarts the timer. */
export function showAuthError() {
  shownAt = Date.now();
  notify();
}

export function hideAuthError() {
  shownAt = null;
  notify();
}

/**
 * The sign-in failure message: one sentence for every OAuth failure, never the provider's own
 * text (`details` in the callback URL is English, or the provider's, and is not ours to show).
 *
 * Rendered by `OAuthHandler`, outside the routes' `<Suspense>`, so it reads `common` only.
 */
const AuthErrorBanner = () => {
  const { t } = useTranslation('common');
  const visibleSince = useSyncExternalStore(subscribe, getSnapshot);

  useEffect(() => {
    if (visibleSince === null) return undefined;
    const timer = setTimeout(hideAuthError, AUTH_ERROR_HIDE_MS);
    return () => clearTimeout(timer);
  }, [visibleSince]);

  if (visibleSince === null) return null;

  return (
    <div className="fixed top-4 inset-x-4 z-[100] flex justify-center pointer-events-none">
      <div
        role="alert"
        className="pointer-events-auto flex items-start gap-3 max-w-md w-full rounded-lg border border-destructive bg-destructive text-destructive-foreground px-4 py-3 shadow-soft"
      >
        <Icon name="AlertCircle" size={18} className="mt-0.5 shrink-0" />
        <p className="flex-1 text-sm">{t('errors.authFailed')}</p>
        <button
          type="button"
          onClick={hideAuthError}
          aria-label={t('errors.dismiss')}
          className="shrink-0 rounded p-0.5 hover:bg-destructive-foreground/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-destructive-foreground"
        >
          <Icon name="X" size={16} />
        </button>
      </div>
    </div>
  );
};

export default AuthErrorBanner;
